import { css } from '@linaria/core';
import type { HeaderCellProps } from '../HeaderCell';
import type { SortDirection } from '../types';

const headerSortCell = css`
  cursor: pointer;
  display: flex;
`;

const headerSortCellClassname = `rdg-header-sort-cell ${headerSortCell}`;

const headerSortName = css`
  flex-grow: 1;
  overflow: hidden;
  overflow: clip;
  text-overflow: ellipsis;
`;

const headerSortNameClassname = `rdg-header-sort-name ${headerSortName}`;

type SharedHeaderCellProps<R, SR> = Pick<HeaderCellProps<R, SR>,
  | 'column'
  | 'sorts'
  | 'onSort'
>;

interface Props<R, SR> extends SharedHeaderCellProps<R, SR> {
  children: React.ReactNode;
}

export default function SortableHeaderCell<R, SR>({
  column,
  sorts,
  onSort,
  children
}: Props<R, SR>) {
  const sortIndex = sorts?.findIndex(sort => sort[0] === column.key) ?? -1;
  const sortDirection = sorts?.[sortIndex]?.[1] ?? 'NONE';
  let sortText = '';
  if (sortDirection === 'ASC') {
    sortText = `${sortIndex + 1} \u25B2`;
  } else if (sortDirection === 'DESC') {
    sortText = `${sortIndex + 1} \u25BC`;
  }

  function onClick() {
    if (!onSort) return;
    const { sortDescendingFirst } = column;
    let direction: SortDirection;
    switch (sortDirection) {
      case 'ASC':
        direction = sortDescendingFirst ? 'NONE' : 'DESC';
        break;
      case 'DESC':
        direction = sortDescendingFirst ? 'ASC' : 'NONE';
        break;
      default:
        direction = sortDescendingFirst ? 'DESC' : 'ASC';
        break;
    }
    if (direction === 'NONE') {
      sorts!.splice(sortIndex, 1);
    } else if ((sortDescendingFirst && direction === 'DESC') || (!sortDescendingFirst && direction === 'ASC')) {
      sorts = sorts ?? [];
      sorts.push([column.key, direction, column.sortKeyGetter]);
    } else {
      sorts![sortIndex][1] = direction;
    }
    onSort([...sorts ?? []]);
  }

  return (
    <span className={headerSortCellClassname} onClick={onClick}>
      <span className={headerSortNameClassname}>{children}</span>
      <span>{sortText}</span>
    </span>
  );
}
